import React from 'react';
import { motion } from 'framer-motion';
import {
  BeakerIcon,
  CommandLineIcon,
  CubeIcon,
  ServerIcon,
  ChartPieIcon,
  ShieldCheckIcon,
  ArrowLongRightIcon
} from '@heroicons/react/24/outline';
import { useLanguage } from '../context/LanguageContext';

const SynapsisSolutions = () => {
  const { t } = useLanguage();

  const solutions = [
    {
      titleKey: "solutions.ai.title",
      descriptionKey: "solutions.ai.description",
      featuresKey: "solutions.ai.features",
      icon: BeakerIcon,
    },
    {
      titleKey: "solutions.automation.title",
      descriptionKey: "solutions.automation.description",
      featuresKey: "solutions.automation.features",
      icon: CommandLineIcon,
    },
    {
      titleKey: "solutions.systems.title",
      descriptionKey: "solutions.systems.description",
      featuresKey: "solutions.systems.features",
      icon: CubeIcon,
    },
    {
      titleKey: "solutions.cloud.title",
      descriptionKey: "solutions.cloud.description",
      featuresKey: "solutions.cloud.features",
      icon: ServerIcon,
    },
    {
      titleKey: "solutions.bi.title",
      descriptionKey: "solutions.bi.description",
      featuresKey: "solutions.bi.features",
      icon: ChartPieIcon,
    },
    {
      titleKey: "solutions.security.title",
      descriptionKey: "solutions.security.description",
      featuresKey: "solutions.security.features",
      icon: ShieldCheckIcon,
    }
  ];

  return (
    <section className="py-20 md:py-28 px-4 relative overflow-hidden">
      <div className="absolute inset-0" aria-hidden="true">
        <div className="absolute inset-0 grid-pattern opacity-10" />
      </div>

      <div className="max-w-6xl mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <p className="eyebrow text-accent-400 text-xs mb-3">
            {t('solutions.label')}
          </p>
          <h2 className="text-3xl md:text-5xl font-bold mb-5">
            {t('solutions.title')} <span className="text-accent-400">{t('solutions.titleHighlight')}</span>
          </h2>
          <p className="text-base md:text-lg text-gray-300 max-w-2xl mx-auto">
            {t('solutions.subtitle')}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {solutions.map((solution, index) => {
            const Icon = solution.icon;
            const features = t(solution.featuresKey, '').split(',').filter(Boolean);
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.08 }}
                viewport={{ once: true }}
                whileHover={{ y: -4, transition: { duration: 0.2 } }}
                className="surface rounded-2xl p-6 hover:border-white/15 transition-colors duration-300"
              >
                <div className="w-12 h-12 rounded-xl bg-accent-500/10 border border-accent-500/20 flex items-center justify-center p-2.5 mb-5">
                  <Icon className="w-full h-full text-accent-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{t(solution.titleKey)}</h3>
                <p className="text-sm text-gray-300 leading-relaxed mb-4">{t(solution.descriptionKey)}</p>
                <div className="flex flex-wrap gap-2">
                  {features.map((feature, idx) => (
                    <span
                      key={idx}
                      className="px-2.5 py-1 text-xs font-medium rounded-full bg-white/5 border border-white/10 text-gray-300"
                    >
                      {feature.trim()}
                    </span>
                  ))}
                </div>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          viewport={{ once: true }}
          className="mt-12 text-center"
        >
          <a
            href="#contact"
            className="inline-flex items-center px-6 py-3 rounded-xl surface hover:border-accent-500/40 transition-colors group"
          >
            <span className="font-semibold text-accent-400">
              {t('solutions.contactUs')}
            </span>
            <ArrowLongRightIcon className="w-5 h-5 ml-2 text-accent-400 group-hover:translate-x-1 transition-transform" />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default SynapsisSolutions;
